import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Dropdown, Space } from 'antd';
import { DownOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import { collect } from 'collect.js';
import useHistory from '../../hooks/navigation/useHistory';
import useAuth from '../../hooks/auth/useAuth';
import usePermissions from '../../hooks/auth/usePermissions';
import useScreenType from '../../hooks/context/useScreenType';
import useBrand from '../../hooks/context/useBrand';
import ClickToCopy from '../ClickToCopy';
import { ScreenEnum } from '../../hooks/navigation/Screens';
import LocalizationManager from '../../services/localization/LocalizationManager';
import { operatorLanguages } from '../../services/localization/brandLanguages';
import useApiClient from '../../hooks/api/useApiClient';

export const profile_item = 'profile';
export const language_item = 'language';
export const support_item = 'support';
export const logout_item = 'logout';

const EMPTY_PROFILE = {};

const isFilled = (value) => (typeof value === 'string' ? value.trim() !== '' : value !== null && value !== undefined);

export default function ProfileDropdown() {
  const { t, i18n } = useTranslation(undefined, { keyPrefix: 'Components:Layout:ProfileDropdown' });

  const { company } = useSelector((state) => state.currentCompany);
  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);
  const business_profile = company?.business_profile ?? EMPTY_PROFILE;

  const { logOut } = useAuth();
  const { isMerchant } = usePermissions();
  const history = useHistory();

  const { isDesktop } = useScreenType();
  const brand = useBrand();

  const { ConfigManager } = useApiClient();

  // The partner's languages, primary first, plus this outlet's own configured one — so a
  // manager of a Polish-configured outlet can actually pick Polish for themselves. The
  // header switcher also renders on the login page, where there is no outlet, so it
  // stays on brandLanguages().
  const availableLocales = useMemo(() => operatorLanguages(brand, company?.language), [brand, company?.language]);

  /**
   * Persists the pick to the signed-in manager's own account so it follows them to
   * another browser. Only an explicit pick is persisted — the language BrandProvider
   * aligns to when the brand settles is the partner's, not the operator's choice.
   */
  function selectLanguage(language) {
    i18n.changeLanguage(language);
    ConfigManager.update({ lang: language });
  }

  const onClick = (e) => {
    const action =
      {
        [profile_item]: () => history.push(ScreenEnum.Profile),
        [language_item]: () => selectLanguage(e.key.split('-')[1]),
        [logout_item]: () => logOut(),
      }[collect(e.keyPath).last()] ?? (() => null);

    return action();
  };

  const supportItems = useMemo(
    () =>
      [
        isFilled(serviceProvider?.support_email) && {
          key: 'email',
          label: <a href={`mailto:${serviceProvider.support_email}`}>{serviceProvider.support_email}</a>,
          icon: <MailOutlined />,
        },
        isFilled(serviceProvider?.support_phone_number) && {
          key: 'phone',
          label: <a href={`tel:${serviceProvider.support_phone_number}`}>{serviceProvider.support_phone_number}</a>,
          icon: <PhoneOutlined />,
        },
      ].filter(Boolean),
    [serviceProvider?.support_email, serviceProvider?.support_phone_number],
  );

  const items = useMemo(
    () => [
      {
        key: 'owner_full_name',
        label: (
          <ClickToCopy containerClassName="flex-row-reverse flex justify-between gap-x-2">
            {business_profile.owner_full_name}
          </ClickToCopy>
        ),
        'should-render': !!company?.id && isFilled(business_profile.owner_full_name),
      },
      {
        key: 'owner_email',
        label: (
          <ClickToCopy containerClassName="flex-row-reverse flex justify-between gap-x-2">
            {business_profile.owner_email}
          </ClickToCopy>
        ),
        'should-render': !!company?.id && isFilled(business_profile.owner_email),
      },
      {
        key: 'owner_phone_number',
        label: (
          <ClickToCopy containerClassName="flex-row-reverse flex justify-between gap-x-2">
            {business_profile.owner_phone_number}
          </ClickToCopy>
        ),
        'should-render': !!company?.id && isFilled(business_profile.owner_phone_number),
      },
      {
        key: 'company_uuid',
        label: (
          <ClickToCopy containerClassName="flex-row-reverse flex justify-between gap-x-2" value={company.uuid}>
            Partner ID
          </ClickToCopy>
        ),
        'should-render': !!company?.id && isFilled(company?.uuid),
      },
      {
        key: profile_item,
        label: t(`items.${profile_item}`),
        'should-render': !!company?.id,
      },
      {
        key: language_item,
        label: t(`items.${language_item}`),
        'should-render': availableLocales.length > 1,
        children: availableLocales.map((item) => ({
          key: `locale-${item}`,
          label: LocalizationManager.getLanguageName(item, i18n.language),
        })),
      },
      {
        key: support_item,
        label: t(`items.${support_item}`),
        'should-render': isMerchant && supportItems.length > 0,
        children: supportItems,
      },
      {
        type: 'divider',
        'should-render': true,
      },
      {
        key: logout_item,
        label: t(`items.${logout_item}`),
        'should-render': true,
      },
    ],
    [isMerchant, availableLocales, i18n.language, supportItems, business_profile, company?.id, company?.uuid],
  );

  return (
    company && (
      <Dropdown menu={{ items: collect(items).where('should-render', true).toArray(), onClick }}>
        <a onClick={(e) => e.preventDefault()} className="!text-brand-text-default cursor-pointer">
          <Space size={12} wrap>
            {isDesktop && (company?.id ? business_profile?.owner?.name : serviceProvider?.name)}
            <DownOutlined />
          </Space>
        </a>
      </Dropdown>
    )
  );
}
